import { createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { SCREEN } from '../../../common/selectCurrencyUnit/constant'
import { calculateDefaultUnit } from '../../../common/selectCurrencyUnit/slice'
import { handleExport } from '../../../helpers/export'
import { COMPANY_TYPES } from '../../../utils/common'
import { mappingApiTemplate } from '../../common/helper'
import { companyTypes } from '../constant'
import { UNIT_FINANCIAL } from '../panels/keyFinancials/helper'
import SummaryService from './SummaryService'

const screen = SCREEN.SUMMARY

export const getSummaryHighlight = createAsyncThunk(
  'companyAnalysis/summary/getSummaryHighlight',
  async ({ OrganizationId, companyType }, { rejectWithValue, dispatch }) => {
    try {
      const response = await SummaryService.getSummaryHighlight({
        OrganizationId,
      })
      if (response.success) {
        const year = new Date(response.data?.fiscalYearEnd).getFullYear()
        const getValues = () =>
          companyType === COMPANY_TYPES.CORPORATE
            ? [{ value: response.data?.charterCapital, year }]
            : [
                { value: response.data?.charterCapital, year },
                { value: response.data?.totalAsset, year },
              ]

        dispatch(calculateDefaultUnit({ screen, getValues }))
        return response.data
      }
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

export const getSummaryOverview = createAsyncThunk(
  'companyAnalysis/summary/getSummaryOverview',
  async (params, { rejectWithValue }) => {
    try {
      const response = await SummaryService.getSummaryOverview(params)
      if (response.success) return response.data
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

export const getOwnershipAndGroupInfo = createAsyncThunk(
  'companyAnalysis/summary/getOwnershipAndGroupInfo',
  async (params, { rejectWithValue }) => {
    try {
      const response = await SummaryService.getOwnershipAndGroupInfo(params)
      if (response.success) return response.data
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

export const getOwnershipAndGroup = createAsyncThunk(
  'companyAnalysis/summary/getOwnershipAndGroup',
  async (params, { rejectWithValue }) => {
    try {
      const response = await SummaryService.getOwnershipAndGroup(params)
      if (response.success) return response.data
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

export const getKeyFinancials = createAsyncThunk(
  'companyAnalysis/summary/getKeyFinancials',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await SummaryService.getKeyFinancials(params)
      if (response.success) {
        const getValues = () =>
          response.data
            .filter((item) => item.unit === UNIT_FINANCIAL.VND)
            .map((item) => {
              const latestValue = item.values?.[item.values.length - 1]
              return { value: latestValue?.value, year: latestValue?.realYear }
            })
        dispatch(calculateDefaultUnit({ screen, getValues }))
        return response.data
      }
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

export const getScore = createAsyncThunk(
  'companyAnalysis/summary/getScore',
  async (params, { rejectWithValue }) => {
    try {
      const response = await SummaryService.getScore(params)
      if (response.success) return response.data
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

export const getRecentNews = createAsyncThunk(
  'companyAnalysis/summary/getRecentNews',
  async (params, { rejectWithValue }) => {
    try {
      const response = await SummaryService.getRecentNews(params)
      if (response.success) return response.data
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

export const getAlertHighlight = createAsyncThunk(
  'companyAnalysis/summary/getAlertHighlight',
  async (params, { rejectWithValue }) => {
    try {
      const response = await SummaryService.getAlertHighlight(params)
      if (response.success) return response.data
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

export const getCompanyAlerts = createAsyncThunk(
  'companyAnalysis/summary/getCompanyAlerts',
  async (params, { rejectWithValue }) => {
    try {
      const response = await SummaryService.getCompanyAlerts(params)
      if (response.success) return response.data
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

export const getRevenue = createAsyncThunk(
  'companyAnalysis/summary/getRevenue',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await SummaryService.getRevenue(params)
      if (response.success) {
        const getValues = () => [
          {
            value: response.data?.value,
            year: response.data?.year,
            exchangeRateType: response.data?.exchangeRateType,
          },
        ]
        dispatch(calculateDefaultUnit({ screen, getValues }))
        return response.data
      }
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

export const getIndustryTopYear = createAsyncThunk(
  'companyAnalysis/summary/getIndustryTopYear',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await SummaryService.getIndustryTopYear(params)
      if (response.success) {
        return response.data?.[0]
      }
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

export const getTopIndustryPlayers = createAsyncThunk(
  'companyAnalysis/summary/getTopIndustryPlayers',
  async ({ companyType, ...params }, { rejectWithValue, dispatch }) => {
    try {
      const response = await SummaryService.getTopIndustryPlayers(params)
      if (response.success) {
        const data = (response?.data || []).map((item) => ({
          ...item,
          value:
            companyType === COMPANY_TYPES.BANK
              ? item.totalAssets
              : item.netRevenue,
        }))
        const getValues = () =>
          data.map((item) => ({
            value: item.value,
            year: params.Year,
          }))
        dispatch(calculateDefaultUnit({ screen, getValues }))
        return data
      }
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

const getComparableField = (item, keys) => {
  const key = keys.find((field) => item?.[field] !== undefined)
  return key ? item[key] : undefined
}

const parseComparableBoolean = (value) =>
  value === true || value === 1 || value === '1' || value === 'true'

const getComparableRedirectPermission = (item) => {
  const permission = getComparableField(item, [
    'allowRedirectLink',
    'AllowRedirectLink',
    'allowView',
    'AllowView',
  ])

  return permission === undefined
    ? Boolean(
        getComparableField(item, [
          'similarOrganizationId',
          'SimilarOrganizationId',
        ]),
      )
    : parseComparableBoolean(permission)
}

const normalizeTopComparablePlayers = (responseData) => {
  const rawData = Array.isArray(responseData)
    ? responseData
    : responseData?.items || responseData?.data || []

  const normalized = rawData.map((item) => ({
    ...item,
    publicDateId: getComparableField(item, ['publicDateId', 'PublicDateId']),
    similarOrganizationId: getComparableField(item, [
      'similarOrganizationId',
      'SimilarOrganizationId',
    ]),
    similarOrganizationName: getComparableField(item, [
      'similarOrganizationName',
      'SimilarOrganizationName',
      'enSimilarOrganizationName',
      'en_SimilarOrganizationName',
    ]),
    similarOrganizationNameVi: getComparableField(item, [
      'similarOrganizationNameVi',
      'viSimilarOrganizationName',
      'vi_SimilarOrganizationName',
      'similarOrganizationName',
      'SimilarOrganizationName',
    ]),
    similarityScore: Number(
      getComparableField(item, ['similarityScore', 'SimilarityScore']),
    ),
    industryName: getComparableField(item, [
      'industryName',
      'mainVSICName',
      'vsicName',
      'enVSICName',
      'en_VSICName',
      'VSICName',
    ]),
    industryNameVi: getComparableField(item, [
      'industryNameVi',
      'mainVSICNameVi',
      'viVSICName',
      'vi_VSICName',
      'mainVSICName',
      'VSICName',
    ]),
    companySizeName: getComparableField(item, [
      'companySizeName',
      'enCompanySizeName',
      'en_CompanySizeName',
      'CompanySizeName',
    ]),
    companySizeNameVi: getComparableField(item, [
      'companySizeNameVi',
      'viCompanySizeName',
      'vi_CompanySizeName',
      'CompanySizeName',
    ]),
    investmentTypeName: getComparableField(item, [
      'investmentTypeName',
      'enInvestmentTypeName',
      'en_InvestmentTypeName',
      'InvestmentTypeName',
    ]),
    investmentTypeNameVi: getComparableField(item, [
      'investmentTypeNameVi',
      'viInvestmentTypeName',
      'vi_InvestmentTypeName',
      'InvestmentTypeName',
    ]),
    internationalTradeName: getComparableField(item, [
      'internationalTradeName',
      'enInternationalTradeName',
      'en_InternationalTradeName',
      'InternationalTradeName',
    ]),
    internationalTradeNameVi: getComparableField(item, [
      'internationalTradeNameVi',
      'viInternationalTradeName',
      'vi_InternationalTradeName',
      'InternationalTradeName',
    ]),
    allowRedirectLink: getComparableRedirectPermission(item),
  }))

  const publicDates = normalized
    .map((item) => item.publicDateId)
    .filter((value) => value !== undefined && value !== null)
  const latestPublicDate = publicDates.sort((a, b) =>
    String(b).localeCompare(String(a), undefined, { numeric: true }),
  )[0]
  const latestData = latestPublicDate
    ? normalized.filter(
        (item) => String(item.publicDateId) === String(latestPublicDate),
      )
    : normalized

  return latestData
    .sort((a, b) => (b.similarityScore || 0) - (a.similarityScore || 0))
    .slice(0, 10)
    .map((item, index) => ({ ...item, rank: index + 1 }))
}

export const getTopComparablePlayers = createAsyncThunk(
  'companyAnalysis/summary/getTopComparablePlayers',
  async (params, { rejectWithValue }) => {
    try {
      const response = await SummaryService.getTopComparablePlayers({
        ...params,
        Ishistory: 0,
        recordstatusid: 1,
      })
      if (response.success) return normalizeTopComparablePlayers(response.data)
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

export const downloadTopComparablePlayers = createAsyncThunk(
  'companyAnalysis/summary/downloadTopComparablePlayers',
  async (params) => {
    const response = await SummaryService.downloadTopComparablePlayers({
      ...params,
      Ishistory: 0,
      recordstatusid: 1,
    })
    handleExport(response.data, response.filename)
  },
)

export const getFilingStatus = createAsyncThunk(
  'companyAnalysis/summary/getFilingStatus',
  async (params, { rejectWithValue }) => {
    try {
      const response = await SummaryService.getFilingStatus(params)
      if (response.success) return response.data
      return rejectWithValue({ loading: false })
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ loading: true })
      }
      return rejectWithValue({ loading: false })
    }
  },
)

export const getTemplatesThunk = createAsyncThunk(
  'companyAnalysis/summary/GET_TEMPLATES',
  async (data, { rejectWithValue }) => {
    try {
      const response = await SummaryService.getTemplates(data)
      if (response.success) {
        return {
          ...response,
          data: mappingApiTemplate(response.data || [], data.TemplateType),
        }
      }
      return rejectWithValue(false)
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue(true)
      }
      return rejectWithValue(false)
    }
  },
)

export const getTemplateDetailThunk = createAsyncThunk(
  'companyAnalysis/summary/GET_TEMPLATE_DETAIL',
  async (data, { rejectWithValue }) => {
    const response = await SummaryService.getTemplateDetail(data.params)
    if (response.success) {
      return {
        data: response.data,
        companyType: data.companyType,
        userTypeId: data.userTypeId,
      }
    }
    return rejectWithValue(false)
  },
)

export const setDefaultTemplateThunk = createAsyncThunk(
  'companyAnalysis/summary/SET_DEFAULT_TEMPLATE',
  async (data, { dispatch }) => {
    const response = await SummaryService.setDefaultTemplate(data.param)
    if (response.success) {
      dispatch(getTemplatesThunk(data.paramGet))
      dispatch(getDefaultTemplateThunk(data.paramGet))
    }
  },
)

export const deleteTemplatesThunk = createAsyncThunk(
  'companyAnalysis/summary/DELETE_TEMPLATES',
  async (data, { dispatch }) => {
    const response = await SummaryService.deleteTemplates(data.param)
    if (response.success) {
      dispatch(getTemplatesThunk(data.paramGet))
    }
  },
)

export const updateTemplateThunk = createAsyncThunk(
  'companyAnalysis/summary/UPDATE_TEMPLATE',
  async (data, { dispatch }) => {
    const response = await SummaryService.updateTemplate(data.param)
    if (response.success) {
      dispatch(getTemplatesThunk(data.paramGet))
    }
  },
)

export const getDefaultTemplateThunk = createAsyncThunk(
  'companyAnalysis/summary/GET_DEFAULT_TEMPLATE',
  async (data, { rejectWithValue }) => {
    try {
      const response = await SummaryService.getDefaultTemplate(data)
      if (response.success) {
        return response.data
      }
      return rejectWithValue(false)
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue(true)
      }
      return rejectWithValue(false)
    }
  },
)

export const getDefaultTemplateForLayoutThunk = createAsyncThunk(
  'companyAnalysis/summary/GET_DEFAULT_TEMPLATE_FOR_LAYOUT',
  async ({ data, companyType, userTypeId }, { rejectWithValue }) => {
    const response = await SummaryService.getDefaultTemplate(data)
    if (response.success) {
      return { result: response.data, companyType, userTypeId }
    }
    return rejectWithValue()
  },
)

export const getTemplatesSearchThunk = createAsyncThunk(
  'companyAnalysis/summary/GET_TEMPLATES_SEARCH',
  async (data, { rejectWithValue }) => {
    try {
      const response = await Promise.all(
        companyTypes.map((type) =>
          SummaryService.getTemplates({
            SummaryTemplateName: data,
            Page: 1,
            PageSize: 3,
            TemplateType: type,
          }),
        ),
      )
      const result = {}
      companyTypes.forEach(
        (type, index) =>
          (result[type] = mappingApiTemplate(response[index].data || [], type)),
      )
      return result
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue(true)
      }
      return rejectWithValue(false)
    }
  },
)
